import AppleStore from "assets/images/apple-app-store-badge.png";
import GoogleStore from "assets/images/google-play-badge.png";
import { Card } from "common/styleguide/Card";
import { CTA } from "common/styleguide/CTA";
import fetchJSON from "lib/fetchJSON";
import { featureNames, Msg } from "lib/localization";
import * as libPhone from "libphonenumber-js";
import React from "react";
import { defineMessages, FormattedMessage as T } from "react-intl";
import Select from "react-select";
import styles from "./EmptyRides.scss";
import recordGraphic from "./record-graphic.svg";

const eRidesMessage = defineMessages({
  error: {
    id: "eRidesMessage.error",
    defaultMessage: "We had an issue sending your text, please check your phone number and try again.",
  },
});

export class EmptyRides extends React.PureComponent {
  state = {
    messageType: "install_link_ios",
    phoneNumber: "",
    error: null,
    smsState: null,
  };

  onSubmit = (e) => {
    e.preventDefault();
    const { messageType, phoneNumber, smsState } = this.state;
    if (smsState === "pending") {
      return;
    }

    const parsed = libPhone.parse(phoneNumber, { country: { default: "US" } });

    if (!parsed.phone || !libPhone.isValidNumber(parsed)) {
      this.setState({
        error: <Msg m={eRidesMessage.error} />,
      });
      return;
    }

    const body = {
      phone_number: libPhone.format(parsed, "International_plaintext"),
      message_type: messageType,
    };

    this.setState({ smsState: "pending", error: null });
    fetchJSON(`/sms_messages`, body, { method: "POST" })
      .then(() => this.setState({ smsState: "success" }))
      .catch(() =>
        this.setState({
          smsState: null,
          error: <Msg m={eRidesMessage.error} />,
        })
      );
  };

  render() {
    const { messageType, phoneNumber, smsState, error } = this.state;
    return (
      <div className={styles.EmptyRides}>
        <h2 data-heading-alt>
          <Msg m={featureNames.rides} />
        </h2>
        <Card className={styles.mainCard}>
          <img className={styles.recordGraphic} src={recordGraphic} alt="phone recording ride" />
          <h1 data-heading-alt>
            <T id="EmptyRides.recordRides" defaultMessage="Record Rides" />
          </h1>
          {navigator.language.slice(0, 2) !== "en" ? (
            <div className={styles.StoreButtonsContainer}>
              <div className={styles.leadText}>
                <h2>
                  <T
                    id="EmptyRides.rideAndNavIntl"
                    defaultMessage="Record rides and navigate routes using the Ride with GPS mobile app. Download the app to get started."
                  />
                </h2>
              </div>
              <a href="https://play.google.com/store/apps/details?id=com.ridewithgps.mobile&utm_campaign=android_app&utm_source=site&utm_medium=android_page">
                <img src={GoogleStore} />
              </a>
              <a href="https://itunes.apple.com/us/app/ride-with-gps-bike-computer/id893687399?ls=1&mt=8">
                <img src={AppleStore} />
              </a>
            </div>
          ) : (
            <>
              <div className={styles.leadText}>
                <h2>
                  <T
                    id="EmptyRides.rideAndNav"
                    defaultMessage="Record rides and navigate routes using the Ride with GPS mobile app. Enter your mobile phone number to get started."
                  />
                </h2>
              </div>
              <form className={styles.form} onSubmit={this.onSubmit}>
                <div className={styles.compoundInput}>
                  <div className={styles.Select}>
                    <Select
                      searchable={false}
                      clearable={false}
                      value={messageType}
                      onChange={(e) => this.setState({ messageType: e.value })}
                      options={[
                        { label: "iOS", value: "install_link_ios" },
                        { label: "Android", value: "install_link_android" },
                      ]}
                    />
                  </div>
                  <input
                    value={phoneNumber}
                    onChange={(e) => this.setState({ phoneNumber: e.target.value })}
                    className={styles.phoneInput}
                    id="userphone"
                    type="tel"
                    autoComplete="off"
                    placeholder="(000) 000 - 0000"
                  />
                </div>
                <CTA button type="submit">
                  {smsState === "pending" && <T id="EmptyRides.sending" defaultMessage="Sending..." />}
                  {smsState === "error" && <T id="EmptyRides.retry" defaultMessage="Retry" />}
                  {smsState === "success" && <T id="EmptyRides.linkSent" defaultMessage="Install link sent!" />}
                  {smsState === null && <T id="EmptyRides.sendFree" defaultMessage="Send Free Download" />}
                </CTA>
                {error && <p>{error}</p>}
                <p className={styles.smallText}>
                  *
                  <T
                    id="EmptyRides.neverStore"
                    defaultMessage="We will never store this information, standard rates apply"
                  />
                </p>
              </form>
            </>
          )}
          <>
            <hr />
            <div className={styles.cols}>
              <div>
                <h3>
                  <T id="EmptyRides.gc" defaultMessage="Garmin Connect" />
                </h3>
                <p>
                  <T
                    id="EmptyRides.gcExp"
                    defaultMessage="Sync rides recorded on your Garmin unit to Ride with GPS via Garmin Connect"
                  />
                </p>
                <a data-card-link href="https://ridewithgps.com/help/automatic-sync-with-garmin-connect">
                  <T id="EmptyRides.gcLearn" defaultMessage="Learn more about Garmin Connect" />
                </a>
              </div>
              <div>
                <h3>
                  <T id="EmptyRides.wahoo" defaultMessage="Wahoo Sync" />
                </h3>
                <p>
                  <T
                    id="EmptyRides.wahooExp"
                    defaultMessage="Wahoo ELEMNT and BOLT devices sync automatically with Ride with GPS"
                  />
                </p>
                <a data-card-link href="https://ridewithgps.com/help/wahoo-elemnt">
                  <T id="EmptyRides.wahooLearn" defaultMessage="Learn more about Wahoo Sync" />
                </a>
              </div>
              <div>
                <h3>
                  <T id="EmptyRides.other" defaultMessage="Other Devices" />
                </h3>
                <p>
                  <T
                    id="EmptyRides.otherExp"
                    defaultMessage="Ride with GPS is compatible with most GPS devices and sensors"
                  />
                </p>
                <a data-card-link href="https://ridewithgps.com/help/compatible-devices">
                  <T id="EmptyRides.otherLearn" defaultMessage="Learn More about compatible devices" />
                </a>
              </div>
            </div>
          </>
        </Card>
      </div>
    );
  }
}
